import type { ReactNode } from 'react';
import type { AlertNoteVariant, IActivityAlertItem } from '@apis/activityAlerts';
import { formatShortDate, currentLang } from '@utils/dateUtils';
import { logisticsNoteLine as noteLine } from '@d-orca/shared/client/logistics';
import styles from './activityAlertsCard.module.css';

/* Split out of ActivityAlertsCard: the card is a long file and this is the one
   self-contained piece of it — a pure alert -> copy mapping with no state, no
   query and no layout, exercised through the card's own tests. Keeping it here
   means the switch can grow a case per alert type without the card growing
   with it. */

/**
 * The row's detail line — states the alert's own facts (dates, from→to,
 * plan-vs-done) so the reader never has to fetch them elsewhere. Every dated
 * variant falls back to the plain copy when its context fields are absent
 * (rows written before the fields existed; gone within the 7-day TTL).
 */
export function detailLine(
  alert: IActivityAlertItem,
  t: (key: string, options?: Record<string, unknown>) => string,
  tz: string,
): ReactNode {
  const K = 'dashboard.activityAlerts.detail';
  const day = (value: string) => formatShortDate(value, currentLang(), tz);
  /** "Jun 8 2028" — the year always rides along, so the two ends of a move
   *  can never be mistaken for the same year. */
  const dayWithYear = (value: string) => `${day(value)} ${value.slice(0, 4)}`;
  /** The new value in a before→after change, as an info-blue chip. Colour is
   *  fixed (not severity-driven): this emphasis marks "here's what it became",
   *  a neutral statement of the new fact, not a severity warning. */
  const chip = (text: string): ReactNode => (
    <span className={`${styles.keyChip ?? ''} ${styles.keyInfo ?? ''}`}>{text}</span>
  );
  /** "[struck before] → [blue after]" — the reschedule range and the risk-level
   *  rise share this treatment; only the after chip carries colour. */
  const beforeAfter = (before: string, after: string): ReactNode => (
    <>
      <span className={styles.beforeChip}>{before}</span>
      <span className={styles.chipArrow} aria-hidden="true">{' → '}</span>
      {chip(after)}
    </>
  );
  const count = alert.detailValue;
  switch (alert.type) {
    case 'overdue':
    case 'issue-overdue':
    case 'milestone-missed':
      return alert.endDate
        ? t(`${K}.${alert.type}-dated`, { count, date: day(alert.endDate) })
        : t(`${K}.${alert.type}`, { count });
    case 'due-soon-1d':
      return alert.endDate
        ? t(`${K}.due-soon-1d-dated`, { date: day(alert.endDate) })
        : t(`${K}.due-soon-1d`);
    case 'due-soon-3d':
      return alert.endDate
        ? t(`${K}.due-soon-3d-dated`, { count, date: day(alert.endDate) })
        : t(`${K}.due-soon-3d`, { count });
    case 'rescheduled': {
      // Directional verb (extended / shortened), then the range with years:
      // "Shortened 687 days — [struck Jun 8 2028] → [amber Jul 22 2026]". The
      // signed detailValue carries direction (positive = moved later).
      const verb = t(`${K}.${count >= 0 ? 'rescheduled-later' : 'rescheduled-earlier'}`, { count: Math.abs(count) });
      return alert.previousEndDate && alert.endDate
        ? <>{verb}{' — '}{beforeAfter(dayWithYear(alert.previousEndDate), dayWithYear(alert.endDate))}</>
        : verb;
    }
    case 'scope-added':
      // A new task is not a change — its start–deadline range is stated as
      // plain copy, with no coloured chip that would read as a before→after.
      return alert.startDate && alert.endDate
        ? t(`${K}.scope-added-dated`, { from: day(alert.startDate), to: day(alert.endDate) })
        : t(`${K}.scope-added`);
    case 'completed':
    case 'milestone-reached': {
      // Gated on endDate: legacy rows carried progressPercent (100) here,
      // which must not be read as "100 days early".
      if (!alert.endDate) return t(`${K}.${alert.type}`);
      if (count > 0) return t(`${K}.${alert.type}-early`, { count });
      if (count < 0) return t(`${K}.${alert.type}-late`, { count: -count });
      return t(`${K}.${alert.type}-ontime`);
    }
    case 'risk-up':
      // "Risk level [struck 1/5] → [amber 4/5]" — the product's 0–5 scale,
      // before and after, in the same before/after chips as the reschedule.
      return alert.previousValue != null
        ? <>{t(`${K}.risk-up-label`)}{' '}{beforeAfter(`${alert.previousValue}/5`, `${count}/5`)}</>
        : t(`${K}.risk-up`, { count });
    case 'delayed':
      return alert.previousValue != null
        ? t(`${K}.delayed-detailed`, { val: `${count}%`, plan: alert.previousValue, done: alert.previousValue - count })
        : t(`${K}.delayed`, { val: `${count}%` });
    case 'stalled':
      return alert.sinceDate
        ? t(`${K}.stalled-since`, { count, date: day(alert.sinceDate) })
        : t(`${K}.stalled`, { count });
    case 'issue-opened':
      return alert.endDate
        ? t(`${K}.issue-opened-dated`, { date: day(alert.endDate) })
        : t(`${K}.issue-opened`);
    case 'issue-due-soon':
      return alert.endDate
        ? t(`${K}.issue-due-soon-dated`, { count, date: day(alert.endDate) })
        : t(`${K}.issue-due-soon`, { count });
    case 'issue-rescheduled': {
      // Mirrors the `rescheduled` case exactly: directional verb, then the
      // previous→new due dates in the same struck-before / blue-after chips.
      const verb = t(`${K}.${count >= 0 ? 'issue-rescheduled-later' : 'issue-rescheduled-earlier'}`, { count: Math.abs(count) });
      return alert.previousEndDate && alert.endDate
        ? <>{verb}{' — '}{beforeAfter(dayWithYear(alert.previousEndDate), dayWithYear(alert.endDate))}</>
        : verb;
    }
    case 'issue-priority-raised':
      // The issue twin of activity `risk-up`: the on-time risk level before →
      // after the priority raise, in the same "Risk level X/5 → Y/5" blue chips.
      return alert.previousValue != null
        ? <>{t(`${K}.risk-up-label`)}{' '}{beforeAfter(`${alert.previousValue}/5`, `${count}/5`)}</>
        : t(`${K}.issue-priority-raised`);
    case 'issue-stalled':
      return alert.sinceDate
        ? t(`${K}.issue-stalled-since`, { count, date: day(alert.sinceDate) })
        : t(`${K}.issue-stalled`, { count });
    case 'issue-resolved':
      // Mirrors `completed`: the signed detailValue carries early (+) / late
      // (−); a zero value is "on time" only when a due date backs it, else the
      // plain "Resolved".
      if (count > 0) return t(`${K}.issue-resolved-early`, { count });
      if (count < 0) return t(`${K}.issue-resolved-late`, { count: -count });
      return alert.endDate ? t(`${K}.issue-resolved-ontime`) : t(`${K}.issue-resolved`);
    case 'issue-reopened':
      return count > 0
        ? t(`${K}.issue-reopened-closed-days`, { count })
        : t(`${K}.issue-reopened`);
    case 'stock-gap':
    case 'stock-ran-out':
    case 'delivery-late':
    case 'stock-untracked':
      return logisticsNote(alert, t);
    case 'supplier-declined':
      return t(`${K}.supplier-declined`, { item: alert.title });
  }
}

/**
 * A Deliveries & Stock alert's detail line: the inventory row's OWN note,
 * word for word, as plain text.
 *
 * It renders the row's i18n keys rather than alert-specific copy, so the two
 * surfaces cannot drift into different words for the same fact — the reason the
 * alert stores a `noteVariant` plus the note's figures instead of a finished
 * sentence. Nothing inside is a target: the row it points at is reached through
 * "View in Logistics", not by clicking the sentence.
 *
 * `noteVariant` is absent on alerts written before it existed. Rather than
 * leave those blank (or echo the title, which the row already shows), each
 * group falls back to its primary note — for a stock gap that reproduces the
 * old behaviour exactly, since the gap it interpolates is `detailValue`.
 */
function logisticsNote(
  alert: IActivityAlertItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): ReactNode {
  return logisticsNoteLine(
    alert.noteVariant ?? primaryVariant(alert),
    { value: alert.detailValue, date: alert.endDate, ref: alert.deliveryRef },
    t,
  );
}

/**
 * The sentence a note variant renders, from its bare facts. Shared with the
 * Delivery card, whose "Stock to order" rows carry a live `RowNote` rather
 * than a stored alert — the facts shape matches `RowNote` on purpose, so a
 * note can be passed straight through.
 */
export function logisticsNoteLine(
  variant: AlertNoteVariant,
  facts: { value: number; date?: string; ref?: string },
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return noteLine(variant, facts, t, currentLang());
}

/** The note a group falls back to when the alert predates `noteVariant`. */
function primaryVariant(alert: IActivityAlertItem): AlertNoteVariant {
  switch (alert.type) {
    case 'stock-ran-out': return 'ran-out';
    case 'delivery-late': return 'late-in-flight';
    case 'stock-untracked': return 'untracked-due';
    // A legacy stock-gap carries the gap in `detailValue`, which is the one
    // figure its note needs — so the old row renders unchanged.
    default: return alert.detailValue > 0 ? 'gap' : 'gap-today';
  }
}
